import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Download, X } from 'lucide-react';

export const InstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isInstalled, setIsInstalled] = useState(false);

  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      return;
    }

    // Check if dismissed recently
    const dismissed = localStorage.getItem('pwa-prompt-dismissed');
    if (dismissed) {
      const dismissedTime = parseInt(dismissed, 10);
      const dayAgo = Date.now() - (24 * 60 * 60 * 1000);
      if (dismissedTime > dayAgo) {
        return; // Don't show if dismissed within last 24 hours
      }
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowPrompt(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Check if installed via app installed event
    window.addEventListener('appinstalled', () => {
      setIsInstalled(true);
      setShowPrompt(false);
      setDeferredPrompt(null);
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      setIsInstalled(true);
    }
    
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    localStorage.setItem('pwa-prompt-dismissed', Date.now().toString());
  };

  if (!showPrompt || isInstalled) return null;

  return (
    <div 
      className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:w-80 bg-slate-900 text-white p-4 rounded-lg shadow-2xl z-50 animate-fadeIn"
      data-testid="install-prompt"
    >
      <button 
        onClick={handleDismiss}
        className="absolute top-2 right-2 p-1 hover:bg-slate-700 rounded"
        data-testid="install-prompt-dismiss"
      >
        <X className="h-4 w-4" />
      </button>
      
      <div className="flex items-start gap-3">
        <div className="bg-slate-700 p-2 rounded-lg">
          <Download className="h-6 w-6" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-sm">Install The Watchtower</h3>
          <p className="text-xs text-slate-300 mt-1">
            Add to your home screen for quick access and offline reading.
          </p>
        </div>
      </div>
      
      <div className="flex gap-2 mt-3">
        <Button 
          onClick={handleInstall}
          className="flex-1 bg-white text-slate-900 hover:bg-slate-100 text-sm"
          data-testid="install-prompt-button"
        >
          Install App
        </Button>
        <Button 
          onClick={handleDismiss}
          variant="ghost"
          className="text-slate-300 hover:text-white hover:bg-slate-700 text-sm"
        >
          Not now
        </Button>
      </div>
    </div>
  );
};

export default InstallPrompt;
